import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { TrendingUp, ShoppingBag, Users, Wallet, AlertCircle, BarChart3 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const monthName = (key) => {
  const [y, m] = key.split('-');
  const names = ['Ian', 'Feb', 'Mar', 'Apr', 'Mai', 'Iun', 'Iul', 'Aug', 'Sep', 'Oct', 'Noi', 'Dec'];
  return `${names[parseInt(m, 10) - 1]} ${y}`;
};

const AdminAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/analytics`, { withCredentials: true });
      setData(data);
    } catch (e) {
      toast.error('Eroare la încărcarea rapoartelor');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading || !data) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <p style={{ fontFamily: 'IBM Plex Sans' }}>Se încarcă...</p>
        </div>
      </div>
    );
  }

  const maxMonthly = Math.max(1, ...data.monthly_series.map((m) => m.total));

  const kpi = [
    {
      label: 'Vânzări luna curentă',
      value: `${data.sales_this_month.toFixed(2)} RON`,
      sub: `${data.orders_count_month} comenzi`,
      icon: TrendingUp,
      color: 'text-green-700',
      bg: 'bg-green-50',
      border: 'border-green-200',
      testId: 'kpi-sales-month'
    },
    {
      label: 'Sold neîncasat',
      value: `${data.total_outstanding.toFixed(2)} RON`,
      sub: `${data.unpaid_invoices_count} facturi neachit.`,
      icon: AlertCircle,
      color: 'text-red-700',
      bg: 'bg-red-50',
      border: 'border-red-200',
      testId: 'kpi-outstanding'
    },
    {
      label: 'Total încasat',
      value: `${data.total_paid.toFixed(2)} RON`,
      sub: `din ${data.total_invoiced.toFixed(2)} RON facturați`,
      icon: Wallet,
      color: 'text-blue-900',
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      testId: 'kpi-paid'
    },
    {
      label: 'Clienți / Comenzi',
      value: `${data.total_clients} / ${data.total_orders}`,
      sub: 'toate perioadele',
      icon: Users,
      color: 'text-orange-700',
      bg: 'bg-orange-50',
      border: 'border-orange-200',
      testId: 'kpi-clients-orders'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center space-x-4 mb-8">
          <div className="p-3 bg-blue-100 rounded-lg">
            <BarChart3 className="w-8 h-8 text-blue-900" />
          </div>
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
              Rapoarte & Analitice
            </h1>
            <p className="text-slate-600" style={{ fontFamily: 'IBM Plex Sans' }}>
              Sinteză vânzări, facturi și clienți
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {kpi.map((k) => {
            const Icon = k.icon;
            return (
              <Card key={k.label} className={`border ${k.border} shadow-sm`} data-testid={k.testId}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <p className="text-xs uppercase tracking-wide text-slate-600 font-semibold" style={{ fontFamily: 'IBM Plex Sans' }}>
                      {k.label}
                    </p>
                    <div className={`p-2 rounded-md ${k.bg}`}><Icon className={`w-5 h-5 ${k.color}`} /></div>
                  </div>
                  <p className={`text-2xl md:text-3xl font-bold ${k.color}`} style={{ fontFamily: 'Outfit' }}>
                    {k.value}
                  </p>
                  <p className="text-xs text-slate-500 mt-1" style={{ fontFamily: 'IBM Plex Sans' }}>{k.sub}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="border border-slate-200 shadow-sm" data-testid="chart-monthly">
            <CardHeader className="bg-slate-50 border-b border-slate-200">
              <CardTitle style={{ fontFamily: 'Outfit' }}>Vânzări ultimele 6 luni</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {data.monthly_series.length === 0 ? (
                <p className="text-slate-500 text-sm" style={{ fontFamily: 'IBM Plex Sans' }}>Nu există date.</p>
              ) : (
                <div className="space-y-3">
                  {data.monthly_series.map((m) => (
                    <div key={m.month}>
                      <div className="flex justify-between text-sm mb-1" style={{ fontFamily: 'IBM Plex Sans' }}>
                        <span className="text-slate-700 font-medium">{monthName(m.month)}</span>
                        <span className="font-bold text-blue-900">{m.total.toFixed(2)} RON</span>
                      </div>
                      <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-blue-900 to-orange-500 rounded-full transition-all"
                          style={{ width: `${(m.total / maxMonthly) * 100}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border border-slate-200 shadow-sm" data-testid="top-clients">
            <CardHeader className="bg-slate-50 border-b border-slate-200">
              <CardTitle style={{ fontFamily: 'Outfit' }}>Top 5 clienți (după valoare)</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {data.top_clients.length === 0 ? (
                <p className="text-slate-500 text-sm" style={{ fontFamily: 'IBM Plex Sans' }}>Nu există date.</p>
              ) : (
                <div className="space-y-3">
                  {data.top_clients.map((c, idx) => (
                    <div
                      key={c.user_id}
                      className="flex items-center justify-between p-3 rounded-md bg-slate-50 border border-slate-100"
                      data-testid={`top-client-${idx}`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${idx === 0 ? 'bg-orange-500' : idx === 1 ? 'bg-blue-900' : 'bg-slate-500'}`}>
                          {idx + 1}
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900 text-sm" style={{ fontFamily: 'IBM Plex Sans' }}>{c.name || 'Necunoscut'}</p>
                          <p className="text-xs text-slate-500" style={{ fontFamily: 'IBM Plex Sans' }}>{c.email}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-blue-900" style={{ fontFamily: 'Outfit' }}>{c.total.toFixed(2)} RON</p>
                        <p className="text-xs text-slate-500" style={{ fontFamily: 'IBM Plex Sans' }}>{c.orders} comenzi</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <Card className="mt-6 border border-slate-200 shadow-sm" data-testid="status-breakdown">
          <CardHeader className="bg-slate-50 border-b border-slate-200">
            <CardTitle className="flex items-center space-x-2" style={{ fontFamily: 'Outfit' }}>
              <ShoppingBag className="w-5 h-5 text-blue-900" />
              <span>Distribuție comenzi pe status</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="flex flex-wrap gap-3">
              {Object.entries(data.status_counts).length === 0 ? (
                <p className="text-slate-500 text-sm" style={{ fontFamily: 'IBM Plex Sans' }}>Nu există date.</p>
              ) : (
                Object.entries(data.status_counts).map(([status, count]) => (
                  <Badge key={status} className="bg-blue-100 text-blue-900 border border-blue-200 text-sm py-1 px-3">
                    {status}: <span className="ml-1 font-bold">{count}</span>
                  </Badge>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminAnalytics;
